import { OrderWrapper } from '@/contexts/order-context'
import { AuthProvider } from '@/providers/auth-provider'
import { queryClient } from '@/providers/query-client'
import React, { ReactNode, Suspense, useEffect, useMemo, useState } from 'react'
import { I18nProvider as BaseI18nProvider } from '@/i18n/i18n-react'
import { isLocale } from '@/i18n/i18n-util'
import type { Locales } from '@/i18n/i18n-types'
import { StoredStringSubject } from '@/utils/storage'
import { useGetSubject } from '@/utils/hooks'
import { unsubscribeAll } from '@/utils/rx-utilities'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { ThemeProvider } from '@/providers/theme-provider'
import { Toaster } from '@/components/ui/sonner'
import { tenant } from '@/tenants/tenant'
import { testerModeEnabled } from '@/utils/tester-mode'
import { toast } from 'sonner'

const TanStackRouterDevtools =
	process.env.NODE_ENV === 'production' ?
		() => null
	:	React.lazy(() =>
			import('@tanstack/router-devtools').then(res => ({
				default: res.TanStackRouterDevtools
			}))
		)

const persister = createAsyncStoragePersister({
	storage: window.localStorage
})

const ReactQueryDevtoolsProduction = React.lazy(() =>
	import('@tanstack/react-query-devtools/production').then(d => ({
		default: d.ReactQueryDevtools
	}))
)

export const localeSubject = new StoredStringSubject<Locales>('locale', '')
const I18nProvider = ({ children }: { children: ReactNode }) => {
	const savedLocale = useGetSubject(localeSubject)

	const locale = useMemo(() => {
		const defaultLocale = tenant.features.defaultLocale ?? 'en'
		if (savedLocale) {
			if (isLocale(savedLocale) && tenant.features.allowedLocales.includes(savedLocale)) {
				return savedLocale
			} else {
				localeSubject.next(defaultLocale)
				return defaultLocale
			}
		} else {
			localeSubject.next(defaultLocale)
			return defaultLocale
		}
	}, [savedLocale])

	const onLocaleChanged = (locale: Locales) => {
		localeSubject.next(locale)
	}

	return (
		<BaseI18nProvider initialLocale={locale} onLocaleChanged={onLocaleChanged}>
			{children}
		</BaseI18nProvider>
	)
}

export default function RootProviders({ children }: { children: React.ReactNode }) {
	const [showDevtools, setShowDevtools] = React.useState(false)
	useEffect(() => {
		;(window as any).toggleDevtools = () => setShowDevtools(old => !old)
	}, [])

	const [initialized, setInitialized] = useState(false)
	useEffect(() => {
		setInitialized(true)
		return () => {
			unsubscribeAll()
		}
	}, [])

	useEffect(() => {
		if (testerModeEnabled.value) {
			toast.success('Tester mode active', { duration: 4000 })
		}
	}, [])

	if (!initialized) {
		return null
	}
	return (
		<HelmetProvider>
			<ThemeProvider>
				<I18nProvider>
					<Helmet>
						<title>{tenant.features.name}</title>
					</Helmet>
					<PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
						<AuthProvider>
							<OrderWrapper>
								{children}
								<Toaster />
								<Suspense>
									<TanStackRouterDevtools />
								</Suspense>
								<ReactQueryDevtools initialIsOpen={false} />
								{showDevtools && (
									<Suspense fallback={null}>
										<ReactQueryDevtoolsProduction />
									</Suspense>
								)}
							</OrderWrapper>
						</AuthProvider>
					</PersistQueryClientProvider>
				</I18nProvider>
			</ThemeProvider>
		</HelmetProvider>
	)
}
